/**
 * @module app.HighlightFeature
 */
/**
 * @fileoverview Provides a highlight service which can add, set and remove
 * ol.Features. Use the init function before use it.
 * Each features must have an unique ID (used to manage the feature). Each
 * features can have these optional properties (config) which are used
 * by this services:
 * - @type {string} kind: the style to use by this feature (see 'const' below)
 * - @type {boolean} isProtected: if true, this feature will be not affected by
 *   the clear or remove function (which removes features from overlay).
 * - @type {string} layer the name of the original linked layer. Feature will
 *    be shown only if the corresponding layer is shown (or if the feature
 *    doesn't has this propertie).
 * - @type {boolean} visible (managed by this service !) used to hide or show
 *    this feature.
 */

import appBase from './index.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleCircle from 'ol/style/Circle.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';
import {
  HIGHLIGHT_SPACING,
  HIGHLIGHT_SPACING_SMALL,
  HIGHLIGHT_WIDTH,
  HIGHLIGHT_WIDTH_SMALL,
  MOUNTAINEERING_TYPE_TO_LAYER,
  Z_INDEX
} from './constants.js';
import {ArrowStyle} from "./arrowstyle";


export const KIND_ICONS = {
  // accomodation
  'hotel': 'img/highlight/hotel_22_h.png',
  'bedbreakfast': 'img/highlight/bed_breakfast_22_h.png',
  'youthhostel': 'img/highlight/jugendherberge_22_h.png',
  'backpacker': 'img/highlight/backpacker_22_h.png',
  'groupaccom': 'img/highlight/gruppenunterkunft_22_h.png',
  'sleepingstraw': 'img/highlight/uebernachten_stroh_22_h.png',
  'farmaccom': 'img/highlight/bauernhof_zimmer_22_h.png',
  'vacationapptmt': 'img/highlight/ferienwohnung_22_h.png',
  'mountainhut': 'img/highlight/sac_berghuette_22_h.png',
  'campingsite': 'img/highlight/camping_22_h.png',

  'geoadmin': 'img/highlight/balloon.png',
  'coordinates': 'img/highlight/balloon.png',

  'cycleservice': 'img/highlight/velobikeservice_22_h.png',
  'place': 'img/highlight/ort_22_h.png',
  'sightseeing': 'img/highlight/sightseeing_22_h.png',
  // 'slowup': 'img/highlight/',
  'velorental': 'img/highlight/mietstation_22_h.png',

  'train': 'img/highlight/train_22_h.png',
  'bus': 'img/highlight/bus_22_h.png',
  'trambus': 'img/highlight/trambus_22_h.png',
  'boat': 'img/highlight/boat_22_h.png',
  'cableway': 'img/highlight/cableway_22_h.png',
  'chairlift': 'img/highlight/chairlift_22_h.png',
  'telecabin': 'img/highlight/telecabin_22_h.png',
  'funicular': 'img/highlight/funicular_22_h.png',

  // alpentaxi:
  'seilbahn': 'img/highlight/seilbahn_22_h.png',
  'taxi': 'img/highlight/taxi_22_h.png',
  'rufbus': 'img/highlight/rufbus_22_h.png',

  // mountaineering
  climbing: 'img/highlight/Climbing_22_h.png',
  mountainhiking: 'img/highlight/MountainHiking_22_h.png',
  alpinetour: 'img/highlight/AlpinTour_22_h.png',
  viaferrata: 'img/highlight/ViaFerrata_22_h.png',
  skitour: 'img/highlight/SkiTour_22_h.png'
};


/**
 * @const
 * From http://www.velobuero.ch/farben_chm_mit_Winter.html
 */
export const OUTLINE_COLOR_BY_KIND = {
  'velo': 'rgba(0, 51, 255, 0.6)',
  'wander': 'rgba(0, 102, 0, 0.6)',
  'mountainbike': 'rgba(102, 51, 0, 0.6)',
  'mtb': 'rgba(102, 51, 0, 0.6)',
  'skating': 'rgba(92, 52, 154, 0.6)',
  'kanu': 'rgba(23, 90, 80, 0.6)',
  'winterhiking': 'rgba(206, 5, 156, 0.6)',
  'snowshoe': 'rgba(127, 3, 127, 0.6)',
  'crosscountry': 'rgba(2, 116, 158, 0.6)',
  'sledging': 'rgba(108, 47, 5, 0.6)',
  'slowup': 'rgba(225, 102, 0, 0.6)',
  'transparent': 'rgba(0, 0, 0, 0)'
};


/**
 * @const
 * From http://www.velobuero.ch/farben_chm_mit_Winter.html
 */
export const AREA_COLOR_BY_KIND = {
  'velo': 'rgba(0, 153, 255, 0.6)',
  'wander': 'rgba(0, 204, 0, 0.6)',
  'mountainbike': 'rgba(204, 102, 0, 0.6)',
  'mtb': 'rgba(204, 102, 0, 0.6)',
  'skating': 'rgba(135, 91, 204, 0.6)',
  'kanu': 'rgba(0, 135, 112, 0.6)',
  'winterhiking': 'rgba(255, 132, 255, 0.6)',
  'snowshoe': 'rgba(204, 51, 204, 0.6)',
  'crosscountry': 'rgba(51, 204, 255, 0.6)',
  'sledging': 'rgba(153, 92, 50, 0.6)',
  'slowup': 'rgba(255, 172, 0, 0.6)',
  'transparent': 'rgba(0, 0, 0, 0)'
};


/**
 * @const
 */
export const DEFAULT_STROKE_COLOR = '#fff';

/**
 * @constructor
 */
class HighlightFeature {
  constructor() {

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = null;

    /**
     * @type {app.arrowstyle.ArrowStyle}
     * @private
     */
    this.arrowStyle_ = null;

    /**
     * @type {?angular.Scope}
     * @private
     */
    this.mainScope_ = null;

    /**
     * @type {app.CacheBustService}
     * @private
     */
    this.cacheBustService_;

    /**
     * @type {ol.source.Vector}
     * @private
     */
    this.featureOverlay_ = new olSourceVector();

    /**
     * @type {boolean}
     */
    this.initialized = false;
  }

  /**
   * @param {!ol.Map} map
   * @param {!angular.Scope} mainScope
   * @param {!app.CacheBustService} cacheBustService
   */
  init(map, mainScope, cacheBustService) {
    if (!this.map_) {
      this.map_ = map;
      this.arrowStyle_ = new ArrowStyle(map);
      this.mainScope_ = mainScope;
      this.cacheBustService_ = cacheBustService;

      const featureOverlayLayer = new olLayerVector({
        'name': 'highlight',
        source: this.featureOverlay_,
        'altitudeMode': 'clampToGround',
        style: this.getStyleForFeature_(),
        'style3d': this.getStyleForFeature_(true),
        zIndex: Z_INDEX.HIGHLIGHT
      });
      map.addLayer(featureOverlayLayer);

      this.initialized = true;

      mainScope.$on('layersUpdated', function(event, layers) {
        // Don't use var "layers" because it contains only group informations
        // when all layers in a group are checked.
        this.setVisibility_();
      }.bind(this));
    }
  }

  /**
   * @param {!ol.Feature} feature
   * @param {!string} id
   * @param {Object<string, (boolean|string)>} config
   */
  addFeature(feature, id, config) {
    feature.setId(id);
    if (!this.getFeatureById(id)) {
      feature.set('visible', true);
      this.setFeature(feature, config);
      this.featureOverlay_.addFeature(feature);
    }
  }

  /**
   * @param {!string} id
   * @return {ol.Feature}
   */
  getFeatureById(id) {
    const features = this.featureOverlay_.getFeatures();
    return features.find(feature => feature.getId() === id);
  }

  /**
   * @param {!string} id
   * @param {boolean} force
   */
  removeFeature(id, force) {
    const feature = this.getFeatureById(id);
    console.assert(feature);
    if (force === true || feature.get('isProtected') !== true) {
      this.featureOverlay_.removeFeature(feature);
    }
  }

  /**
   */
  clear() {
    this.featureOverlay_.getFeatures().forEach(function(feature) {
      const id = /** @type {string} */ (feature.getId());
      this.removeFeature(id, false);
    }, this);
  }

  /**
   * @param {!ol.Feature} feature
   * @param {Object<string, (string|ol.geom.Geometry|boolean)>} config
   */
  setFeature(feature, config) {
    const geom = /** @type {?ol.geom.Geometry} */ (config['geom']);
    if (geom) {
      feature.setGeometry(geom);
    }
    delete config['geom'];
    for (const key in config) {
      feature.set(key, config[key]);
    }
  }

  /**
   * @private
   */
  setVisibility_() {
    const layers = this.mainScope_['ctrl']['wms']['layers'];
    this.featureOverlay_.forEachFeature(function(feature) {
      const layer = feature.get('layer');
      // never hide if feature doesn't have a layer.
      if (layer) {
        feature.set('visible', !!layers[layer]);
      }
    }, this);
  }

  /**
   * @private
   * @param {string|undefined} kind
   * @param {boolean} smallResolution
   * @return {!ol.style.Style}
   */
  createOutlineStroke_(kind, smallResolution) {
    return new olStyleStyle({
      stroke: new olStyleStroke({
        color: OUTLINE_COLOR_BY_KIND[kind] || DEFAULT_STROKE_COLOR,
        width: smallResolution ? 8 : 6
      })
    });
  }

  /**
   * @private
   * @param {string|undefined} kind
   * @param {boolean} smallResolution
   * @return {!ol.style.Style}
   */
  createAreaStroke_(kind, smallResolution) {
    return new olStyleStyle({
      stroke: new olStyleStroke({
        color: AREA_COLOR_BY_KIND[kind] || DEFAULT_STROKE_COLOR,
        width: smallResolution ? 6 : 4
      })
    });
  }

  /**
   * @private
   * @param {string|undefined} kind
   * @param {boolean} smallResolution
   * @return {Array.<ol.style.Style>}
   */
  create2DStyle_(kind, smallResolution) {
    const icon = KIND_ICONS[kind];

    let outline = this.createOutlineStroke_(kind, smallResolution);

    let area = this.createAreaStroke_(kind, smallResolution);

    // dots by default
    const dotSpacing = smallResolution ? HIGHLIGHT_SPACING_SMALL : HIGHLIGHT_SPACING;
    const strokeWidth = smallResolution ? HIGHLIGHT_WIDTH_SMALL : HIGHLIGHT_WIDTH;
    let over = new olStyleStyle({
      stroke: new olStyleStroke({
        color: '#e51e1e',
        width: strokeWidth,
        lineDash: [0.001, dotSpacing]
      })
    });

    switch (kind) {
      case 'wildlife':
      case 'alpguarddog':
      case 'swisspark':
        over = null;
        area = null;
        outline = new olStyleStyle({
          stroke: new olStyleStroke({
            color: '#e51e1e',
            width: smallResolution ? 9 : 6
          })
        });
        break;
      case 'slowup':
        outline = null;
        area = null;
        break;
      default:
        // nothing to do
        break;
    }

    const styling = [];

    if (icon) {
      const iconStyle = new olStyleIcon({
        anchor: [0.5, 0.5],
        src: this.cacheBustService_.addSuffix(icon)
      });
      if (MOUNTAINEERING_TYPE_TO_LAYER[kind]) {
        iconStyle.size = 47;
      }
      styling.push(new olStyleStyle({
        image: iconStyle
      }));
    }

    if (outline) {
      styling.push(outline);
    }

    if (area) {
      styling.push(area);
    }

    if (over) {
      styling.push(over);
    }

    return styling;
  }

  /**
   * @private
   * @param {string|undefined} kind
   * @return {Array.<ol.style.Style>}
   */
  create3DStyle_(kind) {
    let icon = '';
    const mountaineeringLayer = MOUNTAINEERING_TYPE_TO_LAYER[kind];
    if (mountaineeringLayer) {
      const key = mountaineeringLayer.replace(' ', '');
      icon = `img/highlight/${key}_22_h.png`;
    } else {
      const key = kind.charAt(0).toUpperCase() + kind.slice(1);
      icon = `img/3dpoi/3d_${key}_22_h.png`;
    }

    let image;
    if (kind === 'transparent') {
      // TODO: check transparent is only for lines
      image = new olStyleCircle({
        radius: 5,
        fill: new olStyleFill({
          color: [229, 30, 30, 1.0] // #e51e1e
        })
      });
    } else if (kind === 'swisspark') {
      return [
        new olStyleStyle({
          stroke: new olStyleStroke({
            color: [229, 30, 30, 0.5], // #e51e1e
            width: 15
          })
        })
      ];
    } else if (kind === 'wildlife' || kind === 'alpguarddog') {
      return [
        new olStyleStyle({
          fill: new olStyleFill({
            color: [229, 30, 30, 0.5] // #e51e1e
          })
        })
      ];
    } else {
      image = new olStyleIcon({
        anchor: mountaineeringLayer ? [0.5, 0.8] : [0.5, 1],
        src: this.cacheBustService_.addSuffix(icon)
      });
    }

    const styling = [
      new olStyleStyle({
        image: image
      })
    ];
    return styling;
  }

  /**
   * @private
   * @param {!ol.Feature} feature
   */
  hasArrowStyle_(feature) {
    let hasArrowStyle = false;
    ['signal_way', 'route_signal_way'].forEach(signalWayProperty => {
      if (feature.get(signalWayProperty) === 'oneway') {
        hasArrowStyle = true;
      }
    });
    return hasArrowStyle;
  }

  /**
   * @private
   * @param {boolean=} opt_is3D
   * @return {!ol.StyleFunction}
   */
  getStyleForFeature_(opt_is3D) {
    return (feature, resolution) => {
      const kind = /** @type {string} */ (feature.get('kind'));
      if (kind && feature.get('visible')) {
        const hasArrowStyle = this.hasArrowStyle_(feature);
        if (opt_is3D) {
          if (hasArrowStyle) {
            return this.arrowStyle_.create3DArrowStyles(feature);
          } else {
            return this.create3DStyle_(kind);
          }
        } else {
          const smallResolution = resolution <= 20;
          if (hasArrowStyle) {
            return [
              this.createOutlineStroke_(kind, smallResolution),
              this.createAreaStroke_(kind, smallResolution),
              ...this.arrowStyle_.create2DArrowStyles(feature, resolution)
            ];
          } else {
            return this.create2DStyle_(kind, smallResolution);
          }
        }
      }
      return [];
    };
  }
}

appBase.service('appHighlightFeature', HighlightFeature);


export default HighlightFeature;
